import { supabase } from './supabase';

export interface KnowledgeBaseArticle {
  id: string;
  title: string;
  content: string;
  section_content?: string;
  summary?: string;
}

export interface KnowledgeBaseSearchResult {
  searchResults: KnowledgeBaseArticle[];
  total: number;
}

export const getArticles = async (): Promise<KnowledgeBaseArticle[]> => {
  const { data, error } = await supabase.from('knowledge_base_articles').select('*');

  if (error) {
    console.error('[knowledge-base] Error fetching articles:', error);
    return [];
  }

  return (data || []) as KnowledgeBaseArticle[];
};

export const getArticle = async (id: string): Promise<KnowledgeBaseArticle | null> => {
  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('[knowledge-base] Error fetching article:', error);
    return null;
  }

  return (data as KnowledgeBaseArticle) ?? null;
};

export const getRelevantContext = async (
  query: string | string[]
): Promise<KnowledgeBaseSearchResult> => {
  const searchPhrase = Array.isArray(query) ? query.join(' ') : query;

  const { data, error } = await supabase
    .from('knowledge_base_articles')
    .select('id,title,content,section_content,summary')
    .ilike('content', `%${searchPhrase}%`)
    .limit(5);

  if (error) {
    console.error('[knowledge-base] Error fetching relevant context:', error);
    return { searchResults: [], total: 0 };
  }

  const articles: KnowledgeBaseArticle[] = (data || []).map((article) => ({
    ...article,
    section_content: article.section_content || article.content,
  }));

  return {
    searchResults: articles,
    total: articles.length,
  };
};

export const formatContextForAI = (articles: KnowledgeBaseArticle[]) => {
  return articles
    .map((article) => `Title: ${article.title}\n${article.section_content ?? article.content}`)
    .join('\n\n---\n\n');
};

export interface CompanyInfoRecord {
  id: string;
  company_id?: string;
  company_name: string;
  company_data: Record<string, any>;
}

export const getCompanyInfo = async (companyId?: string): Promise<CompanyInfoRecord[]> => {
  try {
    const baseQuery = supabase.from('company_info_reference').select('*');

    if (companyId) {
      const { data, error } = await baseQuery.eq('company_id', companyId).maybeSingle();
      if (error || !data) {
        console.error('[knowledge-base] Error fetching company info:', error);
        return [];
      }
      return [data as CompanyInfoRecord];
    }

    const { data, error } = await baseQuery;
    if (error) {
      console.error('[knowledge-base] Error fetching company info list:', error);
      return [];
    }

    return (data || []) as CompanyInfoRecord[];
  } catch (error) {
    console.error('[knowledge-base] Unexpected company info error:', error);
    return [];
  }
};
